import { useEffect, useMemo, useState } from 'react';
import { Flex } from 'antd';
import { Text } from 'components/Base/Typography';
import { Popover } from 'components/Base/Popover';
import Spinner from 'components/Base/Spinner';
import {
  ENotificationType,
  useShowToastNotification,
} from 'components/Base/Notification';
import {
  useGetWorkshopTaskAssigneesQuery,
  useUpdateWorkshopTaskAssigneeMutation,
} from 'redux/apiSlice/workshopTasks.slice';
import { TWorkshopTaskAssigneeUI } from 'data/types/workshopTasks.types';
import { AddPersonOutline, SearchOutline } from 'assets/icon/icon';
import {
  SAssigneePersonItem,
  SAssigneeSearchInput,
  SAssigneeTriggerButton,
} from './WorkshopTaskAssigneeCell.styles';

const SEARCH_DEBOUNCE_MS = 300;

type WorkshopTaskAssigneeCellProps = {
  workshopId: string;
  taskId: string;
  value: string;
  disabled?: boolean;
};

const WorkshopTaskAssigneeCell = ({
  workshopId,
  taskId,
  value,
  disabled,
}: WorkshopTaskAssigneeCellProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedTerm, setDebouncedTerm] = useState('');
  const [updateAssignee, { isLoading: isUpdating }] =
    useUpdateWorkshopTaskAssigneeMutation();
  const { showToastNotification } = useShowToastNotification();

  useEffect(() => {
    if (!isOpen) {
      setSearchTerm('');
      setDebouncedTerm('');
      return undefined;
    }
    const handle = setTimeout(
      () => setDebouncedTerm(searchTerm.trim()),
      SEARCH_DEBOUNCE_MS,
    );
    return () => clearTimeout(handle);
  }, [searchTerm, isOpen]);

  const { data: options = [], isFetching } = useGetWorkshopTaskAssigneesQuery(
    { workshopId, assignee: debouncedTerm },
    { skip: !isOpen },
  );

  const cannotEdit = disabled || isUpdating;

  const handlePersonSelect = (person: TWorkshopTaskAssigneeUI) => async () => {
    setIsOpen(false);
    if (person.name === value) return;
    try {
      await updateAssignee({
        workshopId,
        taskId,
        userId: person.userId,
        userName: person.name,
      }).unwrap();
    } catch {
      showToastNotification({
        message: 'Could not update task assignee. Please try again.',
        type: ENotificationType.ERROR,
      });
    }
  };

  const handleOpenChange = (next: boolean) => {
    if (cannotEdit && next) return;
    setIsOpen(next);
  };

  const popoverContent = useMemo(() => {
    let body: React.ReactNode;
    if (isFetching) {
      body = (
        <Flex align="center" justify="center" style={{ height: 100 }}>
          <Spinner size="large" />
        </Flex>
      );
    } else if (!options.length) {
      body = (
        <Flex align="center" justify="center" style={{ padding: 16 }}>
          <Text>No matching users</Text>
        </Flex>
      );
    } else {
      body = options.map(person => (
        <SAssigneePersonItem
          key={person.userId}
          onClick={handlePersonSelect(person)}
          data-testid="task-assignee-option"
        >
          {person.name}
        </SAssigneePersonItem>
      ));
    }
    return (
      <Flex vertical gap={8} style={{ width: 240 }}>
        <SAssigneeSearchInput
          placeholder="Search"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          autoFocus
          onClick={e => e.stopPropagation()}
          prefix={<SearchOutline fill="#656565" size={18} />}
        />
        <Flex
          vertical
          style={{ maxHeight: 224, overflowY: 'auto', overflowX: 'hidden' }}
        >
          {body}
        </Flex>
      </Flex>
    );
  }, [isFetching, options, searchTerm, handlePersonSelect]);

  return (
    <Popover
      content={popoverContent}
      trigger="click"
      open={isOpen}
      onOpenChange={handleOpenChange}
      placement="bottomLeft"
      arrow={false}
      data-cy="task-assignee-popover"
    >
      <SAssigneeTriggerButton
        type="text"
        icon={!value ? <AddPersonOutline size={22} /> : null}
        style={{
          color: !value ? '#808080' : 'inherit',
          cursor: cannotEdit ? 'not-allowed' : '',
        }}
        data-cy="task-assignee-button"
        data-testid="task-assignee"
        disabled={cannotEdit}
      >
        {value || 'Add'}
      </SAssigneeTriggerButton>
    </Popover>
  );
};

export default WorkshopTaskAssigneeCell;
